import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { MAT_TOOLTIP_DEFAULT_OPTIONS } from '@angular/material/tooltip';
import { RouterOutlet } from '@angular/router';
import { combineLatest, filter, switchMap, take, tap } from 'rxjs';

import { AuthService, TenantService, UserService } from '@celum/authentication';
import { CelumPropertiesProvider, logEmbeddingContext } from '@celum/core';
import { ConfigurationResolver } from '@celum/portals/shared';
import { TRACKING_SERVICE } from '@celum/shared/domain';

@Component({
  imports: [RouterOutlet],
  selector: 'por-root',
  template: `
    <router-outlet></router-outlet>
  `,
  styleUrls: ['./app.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: MAT_TOOLTIP_DEFAULT_OPTIONS,
      useValue: { disableTooltipInteractivity: true, showDelay: 500 }
    }
  ]
})
export class AppComponent implements OnInit {
  private authService = inject(AuthService);
  private userService = inject(UserService);
  private configuration = inject(ConfigurationResolver);
  private tenantService = inject(TenantService);
  private trackingService = inject(TRACKING_SERVICE);

  constructor() {
    // Remove the last stored tenant. It needs to be stored only for the current session, so we have to make sure that there is none at startup
    this.tenantService.removeTenant();
    this.trackingService.init();
    logEmbeddingContext();
  }

  public ngOnInit(): void {
    // Load the user details for authenticated users. Make sure to wait until the portal configuration is loaded, else the call will fail!
    combineLatest([this.authService.isAuthenticated$, this.configuration.configuration$])
      .pipe(
        filter(([isAuthenticated, config]) => isAuthenticated && !!config),
        take(1),
        tap(([_, config]) => {
          this.authService.setLogoutRedirectUriFallback(`${CelumPropertiesProvider.properties.authentication.logoutRedirectUri}/${config.id}`);
        }),
        // TODO: Handle UserInfoLoadingError here (by e.g. using the AppErrorHandler)
        switchMap(() => this.userService.loadCurrentUser())
      )
      .subscribe();
  }
}
